import isNil from 'lodash/isNil'
import { FormControl } from 'react-bootstrap'
import type { CSSProperties, ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, RefObject } from 'react'
import React from 'react'
import toNumber from 'lodash/toNumber'
import styles from './NumberInput.module.scss'
import type { FieldEditorInjectedProps } from '../FieldEditorInjectedProps'
import type { InlineInputInjectedProps } from '../InlineInputInjectedProps'

export type NumberInputProps = {
  submitting?: boolean
  value: number | string
  name?: string
  placeholder?: string
  disabled?: boolean
  min?: number
  max?: number
  step?: number
  onChange?: ChangeEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  style?: CSSProperties
  inputRef?: RefObject<HTMLInputElement> | any
} & FieldEditorInjectedProps &
  InlineInputInjectedProps

export const NumberInput = ({
  submitting,
  value,
  disabled,
  step = 1,
  inputRef,
  min,
  max,
  onBlur,
  onFocus,
  form: _form,
  meta: _meta,
  isDisabled: _isDisabled,
  noLabel: _noLabel,
  saveNow: _saveNow,
  cancelNow: _cancelNow,
  setNewValue: _setNewValue,
  documentId: _documentId,
  initialValues: _initialValues,
  validate: _validate,
  canEdit: _canEdit,
  editable: _editable,
  inputProps: _inputProps,
  ...rest
}: NumberInputProps) => {
  const parseValue = (val: string | number) => {
    return isNil(val) ? '' : val
  }

  const _onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    //we need this to respect the min and max value when the user directly type from the input
    const changeValue = toNumber(e?.target?.value ?? '')

    //if changedValue is more than max, just return
    if (!isNil(max) && changeValue > max) {
      e.target.value = parseValue(max).toString()
      return
    }

    //if changedValue is less than min, just return
    if (!isNil(min) && changeValue < min) {
      e.target.value = parseValue(min).toString()
      return
    }

    //return from the specified onBlur
    if (onBlur) {
      return onBlur(e)
    }
  }

  return (
    <FormControl
      onFocus={onFocus ?? ((e) => e.target.select())}
      ref={inputRef}
      type="number"
      step={step}
      disabled={disabled || submitting}
      value={parseValue(value)}
      max={max}
      min={min}
      onBlur={_onBlur}
      required={true}
      className={styles.hideSpinner}
      {...rest}
    />
  )
}
